import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * SectorCombobox — Selector de sector con barra de búsqueda incorporada.
 *
 * Reemplaza el <Select> tradicional de shadcn cuando la lista de sectores es larga.
 * Filtra en tiempo real por nombre del sector (case-insensitive, ignora acentos).
 *
 * Props:
 *  - value: string  — nombre del sector actualmente seleccionado.
 *  - onChange: (nombre: string) => void
 *  - sectores: Array<{id: string, nombre: string}>
 *  - placeholder?: string (default "Seleccione sector")
 *  - emptyText?: string   — mensaje cuando no hay sectores (default "No hay sectores cargados")
 *  - disabled?: boolean
 *  - testId?: string       — data-testid del trigger (equivalente al SelectTrigger anterior)
 *  - triggerClassName?: string
 */
export function SectorCombobox({
  value,
  onChange,
  sectores = [],
  placeholder = "Seleccione sector",
  emptyText = "No hay sectores cargados",
  disabled = false,
  testId,
  triggerClassName,
}) {
  const [open, setOpen] = React.useState(false);

  const normalize = (s) =>
    String(s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-between font-normal bg-white",
            !value && "text-slate-500",
            triggerClassName,
          )}
        >
          <span className="truncate text-left">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        sideOffset={4}
      >
        <Command
          filter={(itemValue, search) => {
            // itemValue es el nombre del sector. Comparamos normalizado.
            return normalize(itemValue).includes(normalize(search)) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder="Buscar sector…"
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <CommandList className="max-h-64">
            {sectores.length === 0 ? (
              <CommandEmpty>{emptyText}</CommandEmpty>
            ) : (
              <>
                <CommandEmpty>Sin resultados.</CommandEmpty>
                <CommandGroup>
                  {sectores.map((sec) => (
                    <CommandItem
                      key={sec.id || sec.nombre}
                      value={sec.nombre}
                      onSelect={(currentValue) => {
                        // cmdk normaliza a lowercase — buscamos el nombre original preservando case
                        const match = sectores.find(
                          (x) => normalize(x.nombre) === normalize(currentValue),
                        );
                        onChange(match?.nombre ?? currentValue);
                        setOpen(false);
                      }}
                      data-testid={testId ? `${testId}-opt-${sec.nombre}` : undefined}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === sec.nombre ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="truncate">{sec.nombre}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SectorCombobox;
